import { forwardRef } from "react";
import Wave from "antd/es/_util/wave";
import styled, { css, useTheme } from "styled-components";
import Box from "../box";
import { useControllableState } from "../../hooks";
import { presetColors } from "../../utils/style-util";
import { CloseOutlined } from "../../icons";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const statusColors = {
  processing: "info",
  success: "positive",
  error: "negative",
  warning: "warning"
};
const CloseIcon = styled(CloseOutlined).withConfig({
  displayName: "tag__CloseIcon",
  componentId: "sc-1chiame-0"
})(["display:inline-block;margin-left:4px;font-size:10px;font-weight:bold;transition:all 0.3s ", ";cursor:pointer;"], p => p.theme.transitions.easeInOutCircle);
const StyledTag = styled(Box).withConfig({
  displayName: "tag__StyledTag",
  componentId: "sc-1chiame-1"
})(["display:inline-block;height:auto;padding:0 8px;max-width:100%;min-height:20px;line-height:20px;white-space:nowrap;font-size:", "px;border-width:1px;border-style:", ";border-radius:", "px;transition:all 0.3s ", ";opacity:1;> a:first-child:last-child{display:inline-block;margin:0 -8px;padding:0 8px;}> span:not(.anticon-close):first-child{margin-right:4px;}", " ", ""], p => p.theme.fontSizes.sm, p => p.dashed ? "dashed" : "solid", p => p.theme.radii.base, p => p.theme.transitions.easeInOutCircle, p => p.disabled && css(["&,&:hover,&:focus,&:active{background:", ";border-color:", ";color:", ";cursor:not-allowed;}"], p.ghost ? "transparent" : p.theme.colors.light.bg, p.theme.colors.border.base, p.theme.colors.disabled), p => p.onClick && css(["user-select:none;cursor:pointer;"]));
const StyledCheckableTag = styled(StyledTag).withConfig({
  displayName: "tag__StyledCheckableTag",
  componentId: "sc-1chiame-2"
})(["background-color:transparent;border:none;user-select:none;cursor:pointer;", " ", " &:active{color:#fff;background-color:", ";}", ""], p => p.checked && css(["color:#fff;background-color:", ";"], p => p.theme.colors.primary.base), p => !p.checked && css(["&:hover{color:", ";}"], p => p.theme.colors.primary.base), p => p.theme.colors.primary.active, p => p.disabled && css(["&,&:hover,&:active{color:", ";background-color:", ";cursor:not-allowed;}"], p.theme.colors.disabled, p.checked ? p.theme.colors.bg.base : p.theme.colors.bg.component));
const CheckableTag = /*#__PURE__*/forwardRef((_ref, ref) => {
  let {
    checked: value,
    defaultChecked = false,
    disabled,
    color,
    onChange,
    onClick,
    ...rest
  } = _ref;
  const [checked, setChecked] = useControllableState(value, onChange, defaultChecked);
  const handleClick = event => {
    if (!disabled) {
      setChecked(!checked);
    }
    onClick?.(event);
  };
  return /*#__PURE__*/_jsx(StyledCheckableTag, {
    ref: ref,
    ...rest,
    checked: checked,
    disabled: disabled,
    onClick: handleClick,
    color: color
  });
});
export const Tag = /*#__PURE__*/forwardRef((_ref2, ref) => {
  let {
    color = "default",
    visible: visibleProp,
    ghost,
    closable = false,
    onClose,
    children,
    ...rest
  } = _ref2;
  const isStatusColor = Object.keys(statusColors).includes(color);
  const isPresetColor = presetColors.includes(color);
  const needsWave = ("onClick" in rest);
  const theme = useTheme();
  const [visible, setVisible] = useControllableState(visibleProp, onClose, true);
  const getBorderColor = () => {
    if (isStatusColor) {
      const key = color;
      return theme.colors[statusColors[key]][2];
    }
    if (isPresetColor) {
      return theme.colors[color][1];
    }
    if (color !== "default") {
      return "transparent";
    }
    return theme.colors.border.base;
  };
  const getTextColor = () => {
    if (isStatusColor) {
      const key = color;
      return theme.colors[statusColors[key]].base;
    }
    if (isPresetColor) {
      return theme.colors[color].base;
    }
    if (color !== "default") {
      return "white";
    }
    return undefined;
  };
  const getBackgroundColor = () => {
    if (ghost) {
      return "transparent";
    }
    if (isStatusColor) {
      const key = color;
      return theme.colors[statusColors[key]][0];
    }
    if (isPresetColor) {
      return theme.colors[color].light;
    }
    if (color !== "default") {
      return color;
    }
    return theme.colors.gray[1];
  };
  const handleClose = event => {
    event.stopPropagation();
    setVisible(false);
  };
  const node = /*#__PURE__*/_jsxs(StyledTag, {
    ref: ref,
    ...rest,
    hidden: !visible,
    borderColor: getBorderColor(),
    color: getTextColor(),
    bg: getBackgroundColor(),
    children: [children, closable ? /*#__PURE__*/_jsx(CloseIcon, {
      onClick: handleClose
    }) : null]
  });
  return needsWave ? /*#__PURE__*/_jsx(Wave, {
    children: node
  }) : node;
});
if (process.env.NODE_ENV !== "production") {
  Tag.displayName = "Tag";
}
export default Object.assign(Tag, {
  CheckableTag
});